import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenantConfig, useCartStore } from '@webmono/storefront-ui/providers'
import type { Menu, MenuItem, Order, OrderStatus, CustomerSummary } from '@webmono/storefront-ui/types'

const API_BASE = '/api/v1'

async function fetchAPI<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  })

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`)
  }

  return response.json()
}

export function useMenu() {
  const tenant = useTenantConfig()

  return useQuery({
    queryKey: ['menu', tenant.id],
    queryFn: () => fetchAPI<Menu>(`/menu?tenantId=${tenant.id}`),
  })
}

export function useMenuItem(itemId: string) {
  const tenant = useTenantConfig()

  return useQuery({
    queryKey: ['menuItem', tenant.id, itemId],
    queryFn: () => fetchAPI<MenuItem>(`/menu/${itemId}?tenantId=${tenant.id}`),
    enabled: !!itemId,
  })
}

export function useOrder(orderId: string) {
  return useQuery({
    queryKey: ['order', orderId],
    queryFn: () => fetchAPI<Order>(`/orders/${orderId}`),
    enabled: !!orderId,
    refetchInterval: 10000,
  })
}

export function useCreateOrder() {
  const queryClient = useQueryClient()
  const cartId = useCartStore((s) => s.id)
  const items = useCartStore((s) => s.items)
  const totals = useCartStore((s) => s.totals)
  const fulfillmentType = useCartStore((s) => s.fulfillmentType)
  const tableNumber = useCartStore((s) => s.tableNumber)
  const deliveryAddress = useCartStore((s) => s.deliveryAddress)
  const tip = useCartStore((s) => s.tip)
  const clearCart = useCartStore((s) => s.clearCart)
  const tenant = useTenantConfig()

  return useMutation({
    mutationFn: async (data: { customerName: string; customerPhone: string; customerEmail?: string }) => {
      const response = await fetchAPI<{ order: Order }>('/orders', {
        method: 'POST',
        body: JSON.stringify({
          tenantId: tenant.id,
          cartId,
          items,
          totals: {
            ...totals,
            tipCents: tip?.amountCents ?? 0,
            totalCents: totals.totalCents + (tip?.amountCents ?? 0),
          },
          fulfillmentType,
          tableNumber,
          deliveryAddress,
          customer: {
            name: data.customerName,
            phone: data.customerPhone,
            email: data.customerEmail,
          },
        }),
      })
      return response.order
    },
    onSuccess: () => {
      clearCart()
      queryClient.invalidateQueries({ queryKey: ['merchantOrders'] })
    },
  })
}

export function useMerchantOrders() {
  const tenant = useTenantConfig()

  return useQuery({
    queryKey: ['merchantOrders', tenant.id],
    queryFn: () => fetchAPI<Order[]>(`/merchant/orders?tenantId=${tenant.id}`),
    refetchInterval: 5000,
  })
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { orderId: string; status: OrderStatus }) => {
      return fetchAPI<{ order: Order }>(`/merchant/orders/${data.orderId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: data.status }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchantOrders'] })
    },
  })
}

export function useUpdateMenuItem() {
  const queryClient = useQueryClient()
  const tenant = useTenantConfig()

  return useMutation({
    mutationFn: async (data: { itemId: string; available?: boolean; priceCents?: number }) => {
      return fetchAPI<{ item: MenuItem }>(`/merchant/menu/${data.itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ available: data.available, priceCents: data.priceCents }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu', tenant.id] })
    },
  })
}

export function useAnalytics() {
  const tenant = useTenantConfig()

  return useQuery({
    queryKey: ['analytics', tenant.id],
    queryFn: () =>
      fetchAPI<{
        grossSalesCents: number
        netSalesCents: number
        orderCount: number
        averageOrderValueCents: number
        comparisonPeriod: { grossSalesCents: number; orderCount: number }
        topItems: Array<{ itemId: string; name: string; quantity: number; revenueCents: number }>
        hourlyBreakdown: Array<{ hour: number; orderCount: number; revenueCents: number }>
      }>(`/merchant/analytics?tenantId=${tenant.id}`),
  })
}

export function useCustomers(params?: { search?: string }) {
  const tenant = useTenantConfig()
  const searchQuery = params?.search ? `&search=${encodeURIComponent(params.search)}` : ''

  return useQuery({
    queryKey: ['customers', tenant.id, params?.search],
    queryFn: () =>
      fetchAPI<{ customers: CustomerSummary[]; total: number }>(
        `/merchant/customers?tenantId=${tenant.id}${searchQuery}`
      ),
  })
}
